import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Divider,
  Chip,
  Card,
  CardContent,
  Avatar,
  Fade,
  Badge,
  Checkbox,
  FormControlLabel,
  Alert,
  LinearProgress,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Send as SendIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  TableChart as TableChartIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SkipNext as SkipIcon,
  BugReport as JiraIcon,
  CloudDownload as JiraPullIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import PageHeader from '../components/common/PageHeader';
import AlertMessage from '../components/common/AlertMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DataTable, { Column, Action } from '../components/common/DataTable';
import { DataTable as DataTableType, TableColumn, ColumnType } from '../types/table.types';
import { TableData, TableDataQuery } from '../types/tableData.types';
import { TableService } from '../services/table.service';
import { TableDataService } from '../services/tableData.service';
import { selectUser } from '../store/slices/authSlice';
import CopyToTableDialog from './CopyToTableDialog';
import JiraPushDialog from './JiraPushDialog';
import JiraPullDialog from './JiraPullDialog';
import dayjs from 'dayjs';

export default function TableView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const [table, setTable] = useState<DataTableType | null>(null);
  const [data, setData] = useState<TableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [columns, setColumns] = useState<Column[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [mounted, setMounted] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedDataForCopy, setSelectedDataForCopy] = useState<TableData | null>(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [jiraPushOpen, setJiraPushOpen] = useState(false);
  const [jiraPullOpen, setJiraPullOpen] = useState(false);
  const [jiraDataId, setJiraDataId] = useState<string | null>(null);

  const isAdmin = user?.roles.some(role => role.name === 'ADMIN');
  const canEditPresets = user?.roles.some(role => role.name === 'ADMIN' || role.name === 'PM') ?? false;

  useEffect(() => {
    setMounted(true);
    if (id) {
      fetchTable(id);
    }
  }, [id]);

  useEffect(() => {
    if (table) {
      setupColumns();
      fetchData();
    }
  }, [table, page, rowsPerPage, orderBy, order, filters]);

  const fetchTable = async (tableId: string) => {
    try {
      setLoading(true);
      const tableData = await TableService.getTableById(tableId);
      setTable(tableData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки таблицы');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    if (!id) return;

    try {
      setDataLoading(true);
      setDataLoading(true);
      setSelected([]);

      const query: TableDataQuery = {
        page: page + 1,
        pageSize: rowsPerPage,
        sort: { [orderBy]: order.toUpperCase() as 'ASC' | 'DESC' },
      };

      if (Object.keys(filters).length > 0) {
        query.filter = filters;
      }

      const response = await TableDataService.getTableData(id, query);

      const mappedData = response.data.map(item => ({
        ...item,
        ...item.data,
        ownerName: item.owner?.fullName || item.owner?.username || 'Неизвестно',
      }));

      setData(mappedData);
      setTotalCount(response.total);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setDataLoading(false);
    }
  };

  const setupColumns = () => {
    if (!table) return;

    const tableColumns: Column[] = table.columns.map(column => ({
      id: column.name,
      label: column.name,
      sortable: true,
      format: (value) => formatColumnValue(value, column),
    }));

    tableColumns.push(
      {
        id: 'ownerName',
        label: 'Владелец',
        sortable: false,
        format: (value) => value || 'Неизвестно'
      },
      {
        id: 'createdAt',
        label: 'Дата создания',
        sortable: true,
        format: (value) => value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-'
      },
      {
        id: 'updatedAt',
        label: 'Дата обновления',
        sortable: true,
        format: (value) => value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '-'
      }
    );

    setColumns(tableColumns);
  };

  const formatColumnValue = (value: any, column: TableColumn) => {
    if (value === null || value === undefined) {
      return '-';
    }

    switch (column.type) {
      case ColumnType.BOOLEAN:
        return value ? 'Да' : 'Нет';
      case ColumnType.DATE:
        return dayjs(value).format('DD.MM.YYYY');
      case ColumnType.DROPDOWN:
        return (
          <Chip
            label={value}
            size="small"
            color="default"
            variant="outlined"
          />
        );
      default:
        return value.toString();
    }
  };

  const handleCreateData = () => {
    navigate(`/tables/${id}/data/create`);
  };

  const handleEditData = (dataId: string) => {
    navigate(`/tables/${id}/data/${dataId}/edit`);
  };

  const handleViewData = (dataId: string) => {
    navigate(`/tables/${id}/data/${dataId}`);
  };

  const handleViewHistory = (dataId: string) => {
    navigate(`/tables/${id}/data/${dataId}/history`);
  };

  const handleSendData = (dataId: string) => {
    navigate(`/transfers/create?tableId=${id}&dataId=${dataId}`);
  };

  const handleDeleteData = (dataId: string) => {
    setRowToDelete(dataId);
    setDeleteDialog(true);
  };

  const confirmDeleteData = async () => {
    if (!id || !rowToDelete) return;

    try {
      await TableDataService.deleteTableData(id, rowToDelete);
      setData(data.filter(item => item.id !== rowToDelete));
      setTotalCount(totalCount - 1);
      setDeleteDialog(false);
      setRowToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка удаления данных');
    }
  };

  const cancelDeleteData = () => {
    setDeleteDialog(false);
    setRowToDelete(null);
  };

  const confirmBulkDelete = async () => {
    if (!id || selected.length === 0) return;

    const requested = selected.length;

    try {
      setBulkDeleting(true);

      const { deleted } = await TableDataService.bulkDeleteTableData(id, selected);

      setBulkDeleteOpen(false);

      if (deleted < requested) {
        setError(`Удалено ${deleted} из ${requested} — часть записей не найдена или нет прав`);
      }

      fetchData(); // внутри уже есть setSelected([])
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка удаления записей');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSortChange = (newOrderBy: string, newOrder: 'asc' | 'desc') => {
    setOrderBy(newOrderBy);
    setOrder(newOrder);
  };

  const handleFilterOpen = () => {
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
  };

  const handleFilterApply = (newFilters: Record<string, any>) => {
    setFilters(newFilters);
    setPage(0);
    setFilterOpen(false);
  };

  const handleFilterClear = () => {
    setFilters({});
    setPage(0);
    setFilterOpen(false);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleCopyToTable = async (dataId: string) => {
    if (!id) return;
    try {
      const fullData = await TableDataService.getTableDataById(id, dataId);
      setSelectedDataForCopy(fullData);
      setCopyDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    }
  };

  const handleJiraPush = (dataId: string) => {
    setJiraDataId(dataId);
    setJiraPushOpen(true);
  };

  const handleJiraPull = (dataId: string) => {
    setJiraDataId(dataId);
    setJiraPullOpen(true);
  };

  const jiraRow = jiraDataId ? data.find(item => item.id === jiraDataId) ?? null : null;

  const EXPORT_PAGE_SIZE = 100;   // = @Max(100) в QueryTableDataDto
  const EXPORT_MAX_ROWS = 50_000; // предохранитель от случайной выгрузки всей базы

  const fetchAllRowsForExport = async (tableId: string): Promise<TableData[]> => {
    const all: TableData[] = [];
    let pageNum = 1;
    let total = Infinity;

    while (all.length < total && all.length < EXPORT_MAX_ROWS) {
      const query: TableDataQuery = {
        page: pageNum,
        pageSize: EXPORT_PAGE_SIZE,
        sort: { [orderBy]: order.toUpperCase() as 'ASC' | 'DESC' },
      };
      if (Object.keys(filters).length > 0) query.filter = filters;

      const res = await TableDataService.getTableData(tableId, query);
      total = res.total;

      if (res.data.length === 0) break;            // защита от бесконечного цикла
      all.push(...res.data);
      setExportProgress(Math.round((all.length / Math.max(total, 1)) * 100));

      if (res.data.length < EXPORT_PAGE_SIZE) break;
      pageNum++;
    }

    return all;
  };

  const [exportProgress, setExportProgress] = useState(0); // к остальным useState

  const escapeCsv = (value: any): string => {
    let str = value === null || value === undefined ? '' : String(value);
    // защита от формульной инъекции в Excel/Sheets
    if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
    if (/[",\n\r;]/.test(str)) str = `"${str.replace(/"/g, '""')}"`;
    return str;
  };

  const handleExport = async (selectedColumns: string[]) => {
    if (!id || !table) return;

    try {
      setExportLoading(true);
      setExportProgress(0);

      const allRows = await fetchAllRowsForExport(id);

      const headers = selectedColumns.map(colName => {
        if (colName === 'ownerName') return 'Владелец';
        if (colName === 'createdAt') return 'Дата создания';
        if (colName === 'updatedAt') return 'Дата обновления';
        return colName;
      });

      const rows = allRows.map(item =>
        selectedColumns.map(colName => {
          if (colName === 'ownerName') {
            return escapeCsv(item.owner?.fullName || item.owner?.username || '');
          }
          if (colName === 'createdAt' || colName === 'updatedAt') {
            return escapeCsv(item[colName] ? dayjs(item[colName]).format('DD.MM.YYYY HH:mm') : '');
          }
          return escapeCsv(item.data?.[colName] ?? '');
        })
      );

      const csv = '\uFEFF' + [headers.map(escapeCsv).join(','), ...rows.map(r => r.join(','))].join('\r\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${table.name}_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      setExportDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка экспорта данных');
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const actions: Action[] = [
    {
      icon: <ViewIcon />,
      tooltip: 'Просмотр',
      onClick: handleViewData,
      color: 'primary',
    },
    {
      icon: <EditIcon />,
      tooltip: 'Редактировать',
      onClick: handleEditData,
      color: 'primary',
    },
    {
      icon: <HistoryIcon />,
      tooltip: 'История изменений',
      onClick: handleViewHistory,
      color: 'info',
    },
    {
      icon: <SendIcon />,
      tooltip: 'Передать',
      onClick: handleSendData,
      color: 'success',
    },
    {
      icon: <DeleteIcon />,
      tooltip: 'Удалить',
      onClick: handleDeleteData,
      color: 'error',
    },
    {
      icon: <CopyIcon />,
      tooltip: 'Копировать в другую таблицу',
      onClick: handleCopyToTable,
      color: 'info',
    },
    {
      icon: <JiraIcon />,
      tooltip: 'Передать в Jira',
      onClick: handleJiraPush,
      color: 'primary',
    },
    {
      icon: <JiraPullIcon />,
      tooltip: 'Получить данные из Jira',
      onClick: handleJiraPull,
      color: 'success',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (!table) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Таблица не найдена или у вас нет доступа к ней
        </Typography>
        <Button variant="contained" onClick={() => navigate('/tables')} sx={{ mt: 2 }}>
          Вернуться к списку таблиц
        </Button>
      </Box>
    );
  }

  const activeFiltersCount = Object.keys(filters).length;

  return (
    <Box>
      {/* Header with Gradient */}
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            borderRadius: 3,
            p: 4,
            mb: 3,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                <TableChartIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {table.name}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {table.description || 'Просмотр данных таблицы'}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateData}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Добавить запись
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Toolbar */}
      <Fade in={mounted} timeout={600}>
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<TableChartIcon />}
                  label={`Всего записей: ${totalCount}`}
                  sx={{
                    bgcolor: 'rgba(79, 172, 254, 0.1)',
                    color: '#4facfe',
                    fontWeight: 600
                  }}
                />
                {activeFiltersCount > 0 && (
                  <Chip
                    icon={<FilterIcon />}
                    label={`Фильтров: ${activeFiltersCount}`}
                    onDelete={handleFilterClear}
                    deleteIcon={<ClearIcon />}
                    sx={{
                      bgcolor: 'rgba(240, 147, 251, 0.1)',
                      color: '#f093fb',
                      fontWeight: 600
                    }}
                  />
                )}
                {selected.length > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setBulkDeleteOpen(true)}
                    sx={{ ml: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Удалить выбранные ({selected.length})
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Обновить">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      color: '#4facfe',
                      '&:hover': { bgcolor: 'rgba(79, 172, 254, 0.08)' }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Фильтры">
                  <IconButton
                    onClick={handleFilterOpen}
                    sx={{
                      color: activeFiltersCount > 0 ? '#f093fb' : '#4facfe',
                      '&:hover': { bgcolor: 'rgba(79, 172, 254, 0.08)' }
                    }}
                  >
                    <Badge badgeContent={activeFiltersCount} color="secondary">
                      <FilterIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Импорт CSV">
                  <IconButton
                    onClick={() => setImportDialogOpen(true)}
                    sx={{
                      color: '#ff9800',
                      '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.08)' }
                    }}
                  >
                    <ImportIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Экспорт CSV">
                  <IconButton
                    onClick={() => setExportDialogOpen(true)}
                    sx={{
                      color: '#4caf50',
                      '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.08)' }
                    }}
                  >
                    <ExportIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Data Table */}
      <Fade in={mounted} timeout={800}>
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <DataTable
            columns={columns}
            data={data}
            actions={actions}
            loading={dataLoading}
            selectable
            selected={selected}
            onSelectionChange={setSelected}
            pagination={{
              count: totalCount,
              page,
              rowsPerPage,
              onPageChange: handlePageChange,
              onRowsPerPageChange: handleRowsPerPageChange,
            }}
            sorting={{
              orderBy,
              order,
              onSortChange: handleSortChange,
            }}
          />
        </Card>
      </Fade>

      <AlertMessage
        open={!!error}
        message={error || ''}
        severity="error"
        onClose={() => setError(null)}
      />

      <ConfirmDialog
        open={deleteDialog}
        title="Удаление записи"
        message="Вы уверены, что хотите удалить эту запись? Это действие нельзя отменить."
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={confirmDeleteData}
        onCancel={cancelDeleteData}
        severity="error"
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Удаление записей"
        message={`Удалить выбранные записи (${selected.length})? Это действие нельзя отменить.`}
        confirmText={bulkDeleting ? 'Удаление...' : 'Удалить'}
        cancelText="Отмена"
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
        severity="error"
      />

      {/* Filter Dialog */}
      <Dialog
        open={filterOpen}
        onClose={handleFilterClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FilterIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Фильтры
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <FilterForm
            columns={table.columns}
            initialValues={filters}
            onApply={handleFilterApply}
            onClear={handleFilterClear}
            onClose={handleFilterClose}
          />
        </DialogContent>
      </Dialog>

      {/* Copy to Table Dialog */}
      {table && selectedDataForCopy && (
        <CopyToTableDialog
          open={copyDialogOpen}
          onClose={() => {
            setCopyDialogOpen(false);
            setSelectedDataForCopy(null);
          }}
          sourceTable={table}
          sourceData={selectedDataForCopy}
          onSuccess={() => {
            fetchData();
            setCopyDialogOpen(false);
            setSelectedDataForCopy(null);
          }}
        />
      )}

      {/* Export Dialog */}
      {table && (
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          columns={table.columns}
          onExport={handleExport}
          loading={exportLoading}
        />
      )}

      {/* Import Dialog */}
      {table && id && (
        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          tableColumns={table.columns}
          tableId={id}
          onSuccess={() => {
            setImportDialogOpen(false);
            fetchData();
          }}
        />
      )}
      {table && id && (
        <JiraPushDialog
          open={jiraPushOpen}
          onClose={() => { setJiraPushOpen(false); setJiraDataId(null); }}
          tableId={id}
          dataId={jiraDataId}
          row={jiraRow}
          columns={table.columns}
          canEdit={canEditPresets}
        />
      )}

      {table && id && (
        <JiraPullDialog
          open={jiraPullOpen}
          onClose={() => { setJiraPullOpen(false); setJiraDataId(null); }}
          tableId={id}
          dataId={jiraDataId}
          row={jiraRow}
          columns={table.columns}
          canEdit={canEditPresets}
          onApplied={fetchData}
        />
      )}
    </Box>
  );
}

// ==================== Filter Form ====================

interface FilterFormProps {
  columns: TableColumn[];
  initialValues: Record<string, any>;
  onApply: (filters: Record<string, any>) => void;
  onClear: () => void;
  onClose: () => void;
}

function FilterForm({ columns, initialValues, onApply, onClear, onClose }: FilterFormProps) {
  const [filters, setFilters] = useState<Record<string, any>>(initialValues);

  const handleChange = (column: TableColumn, value: any) => {
    if (value === '' || value === null) {
      const newFilters = { ...filters };
      delete newFilters[column.name];
      setFilters(newFilters);
    } else {
      setFilters({ ...filters, [column.name]: value });
    }
  };

  const handleApply = () => {
    onApply(filters);
  };

  const handleClear = () => {
    setFilters({});
    onClear();
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Выберите критерии фильтрации:
      </Typography>

      <Box sx={{ mb: 3 }}>
        {columns.map((column) => (
          <Box key={column.id} sx={{ mb: 2 }}>
            {column.type === ColumnType.TEXT && (
              <TextField
                label={column.name}
                fullWidth
                value={filters[column.name] || ''}
                onChange={(e) => handleChange(column, e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#4facfe' },
                    '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                  }
                }}
              />
            )}

            {column.type === ColumnType.NUMBER && (
              <TextField
                label={column.name}
                fullWidth
                type="number"
                value={filters[column.name] || ''}
                onChange={(e) => handleChange(column, e.target.value ? Number(e.target.value) : '')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#4facfe' },
                    '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                  }
                }}
              />
            )}

            {column.type === ColumnType.DATE && (
              <TextField
                label={column.name}
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filters[column.name] || ''}
                onChange={(e) => handleChange(column, e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#4facfe' },
                    '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                  }
                }}
              />
            )}

            {column.type === ColumnType.BOOLEAN && (
              <TextField
                select
                label={column.name}
                fullWidth
                value={filters[column.name] === undefined ? '' : filters[column.name]}
                onChange={(e) => handleChange(column, e.target.value === '' ? '' : e.target.value === 'true')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#4facfe' },
                    '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                  }
                }}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="true">Да</MenuItem>
                <MenuItem value="false">Нет</MenuItem>
              </TextField>
            )}

            {column.type === ColumnType.DROPDOWN && column.options && column.options.values && (
              <TextField
                select
                label={column.name}
                fullWidth
                value={filters[column.name] || ''}
                onChange={(e) => handleChange(column, e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#4facfe' },
                    '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                  }
                }}
              >
                <MenuItem value="">Все</MenuItem>
                {column.options.values.map((option: string) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="outlined"
          onClick={handleClear}
          startIcon={<ClearIcon />}
          sx={{
            borderColor: '#4facfe',
            color: '#4facfe',
            '&:hover': {
              borderColor: '#4facfe',
              bgcolor: 'rgba(79, 172, 254, 0.08)',
            }
          }}
        >
          Очистить
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          startIcon={<FilterIcon />}
          sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3e9bed 0%, #00d8ed 100%)',
            }
          }}
        >
          Применить
        </Button>
      </Box>
    </Box>
  );
}

// ==================== Export Dialog ====================

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  columns: TableColumn[];
  onExport: (selectedColumns: string[]) => void;
  loading: boolean;
}

function ExportDialog({ open, onClose, columns, onExport, loading }: ExportDialogProps) {
  const allColumns = [
    ...columns.map(c => ({ id: c.name, label: c.name })),
    { id: 'ownerName', label: 'Владелец' },
    { id: 'createdAt', label: 'Дата создания' },
    { id: 'updatedAt', label: 'Дата обновления' },
  ];

  const [selected, setSelected] = useState<string[]>(allColumns.map(c => c.id));

  useEffect(() => {
    if (open) {
      setSelected(allColumns.map(c => c.id));
    }
  }, [open]);

  const handleToggle = (id: string) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selected.length === allColumns.length) {
      setSelected([]);
    } else {
      setSelected(allColumns.map(c => c.id));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
          color: 'white',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ExportIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Экспорт в CSV
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Выберите колонки для экспорта:
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.length === allColumns.length}
                indeterminate={selected.length > 0 && selected.length < allColumns.length}
                onChange={handleSelectAll}
                sx={{
                  color: '#4caf50',
                  '&.Mui-checked': { color: '#4caf50' },
                  '&.MuiCheckbox-indeterminate': { color: '#4caf50' },
                }}
              />
            }
            label={
              <Typography sx={{ fontWeight: 600 }}>
                Выбрать все
              </Typography>
            }
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {allColumns.map(col => (
            <Box
              key={col.id}
              sx={{
                p: 1.5,
                mb: 0.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: selected.includes(col.id) ? '#4caf50' : 'divider',
                bgcolor: selected.includes(col.id) ? 'rgba(76, 175, 80, 0.05)' : 'transparent',
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#4caf50',
                  bgcolor: 'rgba(76, 175, 80, 0.05)',
                },
              }}
              onClick={() => handleToggle(col.id)}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.includes(col.id)}
                    sx={{
                      color: '#4caf50',
                      '&.Mui-checked': { color: '#4caf50' },
                    }}
                  />
                }
                label={col.label}
                sx={{ width: '100%', m: 0 }}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          variant="contained"
          onClick={() => onExport(selected)}
          disabled={selected.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <ExportIcon />}
          sx={{
            background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #43a047 0%, #7cb342 100%)',
            },
          }}
        >
          {loading ? 'Экспорт...' : `Экспорт (${selected.length} колонок)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ==================== CSV Parser ====================

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === '\n' || (char === '\r' && text[i + 1] === '\n')) && !inQuotes) {
      lines.push(current);
      current = '';
      if (char === '\r') i++;
    } else {
      current += char;
    }
  }
  if (current.trim()) {
    lines.push(current);
  }

  const parseLine = (line: string): string[] => {
    const fields: string[] = [];
    let field = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (insideQuotes && line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (ch === ',' && !insideQuotes) {
        fields.push(field.trim());
        field = '';
      } else {
        field += ch;
      }
    }
    fields.push(field.trim());
    return fields;
  };

  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).filter(l => l.trim()).map(parseLine);

  return { headers, rows };
}

// ==================== Import Dialog ====================

type ImportStep = 'upload' | 'mapping' | 'importing' | 'done';

interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  tableColumns: TableColumn[];
  tableId: string;
  onSuccess: () => void;
}

function ImportDialog({ open, onClose, tableColumns, tableId, onSuccess }: ImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<ImportStep>('upload');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep('upload');
      setCsvHeaders([]);
      setCsvRows([]);
      setFileName('');
      setMapping({});
      setImportResult(null);
      setImporting(false);
      setImportProgress(0);
      setParseError(null);
    }
  }, [open]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let text = e.target?.result as string;

        // Убираем BOM
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.slice(1);
        }

        const { headers, rows } = parseCSV(text);

        if (headers.length === 0) {
          setParseError('Файл пуст или не содержит заголовков');
          return;
        }

        if (rows.length === 0) {
          setParseError('Файл не содержит данных (только заголовки)');
          return;
        }

        setCsvHeaders(headers);
        setCsvRows(rows);

        // Автоматический маппинг по совпадению имён
        const autoMapping: Record<string, string> = {};
        tableColumns.forEach(col => {
          const matchIndex = headers.findIndex(
            h => h.toLowerCase().trim() === col.name.toLowerCase().trim()
          );
          if (matchIndex !== -1) {
            autoMapping[col.name] = headers[matchIndex];
          }
        });
        setMapping(autoMapping);
        setStep('mapping');
      } catch (err) {
        setParseError('Ошибка парсинга CSV файла');
      }
    };
    reader.onerror = () => {
      setParseError('Ошибка чтения файла');
    };
    reader.readAsText(file, 'UTF-8');

    // Сброс input чтобы можно было загрузить тот же файл повторно
    event.target.value = '';
  };

  const handleMappingChange = (tableColName: string, csvHeader: string) => {
    setMapping(prev => {
      const newMapping = { ...prev };
      if (csvHeader === '__skip__') {
        delete newMapping[tableColName];
      } else {
        newMapping[tableColName] = csvHeader;
      }
      return newMapping;
    });
  };

  const getMappedColumnsCount = () => Object.keys(mapping).length;

  const handleImport = async () => {
    setImporting(true);
    setImportProgress(0);
    setStep('importing');

    // 1) собрать и типизировать все строки (логика по column.type — как была)
    const allRows: Record<string, any>[] = csvRows.map((row) => {
      const rowData: Record<string, any> = {};
      for (const [tableColName, csvHeader] of Object.entries(mapping)) {
        const csvIndex = csvHeaders.indexOf(csvHeader);
        if (csvIndex === -1) continue;
        const rawValue = row[csvIndex] ?? '';
        const column = tableColumns.find(c => c.name === tableColName);
        if (!column) continue;

        switch (column.type) {
          case ColumnType.NUMBER:
            if (rawValue === '') { if (column.isRequired) rowData[tableColName] = 0; }
            else { const num = Number(rawValue.replace(',', '.')); rowData[tableColName] = isNaN(num) ? rawValue : num; }
            break;
          case ColumnType.BOOLEAN: {
            const lower = rawValue.toLowerCase().trim();
            rowData[tableColName] = ['true', '1', 'да', 'yes'].includes(lower);
            break;
          }
          case ColumnType.DATE:
            if (rawValue) { const p = dayjs(rawValue); rowData[tableColName] = p.isValid() ? p.toISOString() : rawValue; }
            break;
          default:
            if (rawValue !== '' || column.isRequired) rowData[tableColName] = rawValue;
            break;
        }
      }
      return rowData;
    });

    // 2) отправить пачками по 200 — вместо запроса на каждую строку
    const result: ImportResult = { success: 0, failed: 0, errors: [] };
    const CHUNK = 200;

    for (let i = 0; i < allRows.length; i += CHUNK) {
      const slice = allRows.slice(i, i + CHUNK);
      try {
        const res = await TableDataService.bulkCreateTableData(tableId, slice);
        result.success += res.success;
        result.failed += res.failed;
        res.errors.forEach(e => result.errors.push(`Строка ${i + e.row + 2}: ${e.message}`));
      } catch (err: any) {
        result.failed += slice.length;
        result.errors.push(`Строки ${i + 2}–${i + slice.length + 1}: ${err.response?.data?.message || 'ошибка пакета'}`);
      }
      setImportProgress(Math.round((Math.min(i + CHUNK, allRows.length) / allRows.length) * 100));
    }

    setImportResult(result);
    setImporting(false);
    setStep('done');
  };

  const handleClose = () => {
    if (importResult && importResult.success > 0) {
      onSuccess();
    } else {
      onClose();
    }
  };

  const previewRows = csvRows.slice(0, 3);

  return (
    <Dialog
      open={open}
      onClose={importing ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)',
          color: 'white',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ImportIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Импорт из CSV
          </Typography>
          {fileName && (
            <Chip
              label={fileName}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, minHeight: 300 }}>
        {/* ===== Шаг 1: Загрузка файла ===== */}
        {step === 'upload' && (
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              hidden
              onChange={handleFileSelect}
            />

            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: parseError ? '#f44336' : '#ff9800',
                borderRadius: 3,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                bgcolor: parseError ? 'rgba(244, 67, 54, 0.05)' : 'rgba(255, 152, 0, 0.05)',
                '&:hover': {
                  borderColor: parseError ? '#f44336' : '#e65100',
                  bgcolor: parseError ? 'rgba(244, 67, 54, 0.08)' : 'rgba(255, 152, 0, 0.08)',
                },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 64, color: parseError ? '#f44336' : '#ff9800', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Нажмите для выбора CSV файла
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Поддерживаются файлы .csv с разделителем запятая. Первая строка должна содержать заголовки.
              </Typography>
            </Box>

            {parseError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {parseError}
              </Alert>
            )}
          </Box>
        )}

        {/* ===== Шаг 2: Маппинг колонок ===== */}
        {step === 'mapping' && (
          <Box>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Найдено <strong>{csvRows.length}</strong> строк и <strong>{csvHeaders.length}</strong> колонок в файле.
              Сопоставьте колонки CSV с полями таблицы.
            </Alert>

            <TableContainer sx={{ mb: 3, maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.1)', minWidth: 180 }}>
                      Поле таблицы
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.1)', minWidth: 100 }}>
                      Тип
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.1)', minWidth: 200 }}>
                      Колонка CSV
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'rgba(255, 152, 0, 0.1)', minWidth: 200 }}>
                      Превью данных
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableColumns.map(col => {
                    const mappedCsvHeader = mapping[col.name];
                    const csvIndex = mappedCsvHeader ? csvHeaders.indexOf(mappedCsvHeader) : -1;

                    return (
                      <TableRow key={col.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {col.name}
                            </Typography>
                            {col.isRequired && (
                              <Chip label="*" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={col.type}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={mappedCsvHeader || '__skip__'}
                              onChange={(e) => handleMappingChange(col.name, e.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: mappedCsvHeader ? '#ff9800' : 'divider',
                                },
                              }}
                            >
                              <MenuItem value="__skip__">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                  <SkipIcon fontSize="small" />
                                  Пропустить
                                </Box>
                              </MenuItem>
                              {csvHeaders.map(header => (
                                <MenuItem key={header} value={header}>
                                  {header}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {csvIndex !== -1 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {previewRows.slice(0, 2).map((row, i) => (
                                <Typography key={i} variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                  {row[csvIndex] || '—'}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`Сопоставлено полей: ${getMappedColumnsCount()} / ${tableColumns.length}`}
                sx={{
                  bgcolor: getMappedColumnsCount() > 0 ? 'rgba(255, 152, 0, 0.1)' : 'rgba(158,158,158,0.1)',
                  color: getMappedColumnsCount() > 0 ? '#ff9800' : '#9e9e9e',
                  fontWeight: 600,
                }}
              />
              {tableColumns.some(c => c.isRequired && !mapping[c.name]) && (
                <Alert severity="warning" sx={{ py: 0, flex: 1 }}>
                  Не все обязательные поля сопоставлены
                </Alert>
              )}
            </Box>
          </Box>
        )}

        {/* ===== Шаг 3: Импорт ===== */}
        {step === 'importing' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ color: '#ff9800', mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Импорт данных...
            </Typography>
            <Box sx={{ px: 4, mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={importProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: 'rgba(255, 152, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#ff9800',
                    borderRadius: 5,
                  },
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {importProgress}% — обработано {Math.round((importProgress / 100) * csvRows.length)} из {csvRows.length} строк
            </Typography>
          </Box>
        )}

        {/* ===== Шаг 4: Результат ===== */}
        {step === 'done' && importResult && (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {importResult.failed === 0 ? (
                <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 1 }} />
              ) : importResult.success === 0 ? (
                <ErrorIcon sx={{ fontSize: 64, color: '#f44336', mb: 1 }} />
              ) : (
                <CheckCircleIcon sx={{ fontSize: 64, color: '#ff9800', mb: 1 }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Импорт завершён
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center' }}>
              <Card sx={{
                p: 2,
                minWidth: 140,
                textAlign: 'center',
                bgcolor: 'rgba(76, 175, 80, 0.05)',
                border: '1px solid rgba(76, 175, 80, 0.2)',
                borderRadius: 2,
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                  {importResult.success}
                </Typography>
                <Typography variant="body2" color="text.secondary">Успешно</Typography>
              </Card>
              <Card sx={{
                p: 2,
                minWidth: 140,
                textAlign: 'center',
                bgcolor: 'rgba(244, 67, 54, 0.05)',
                border: '1px solid rgba(244, 67, 54, 0.2)',
                borderRadius: 2,
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f44336' }}>
                  {importResult.failed}
                </Typography>
                <Typography variant="body2" color="text.secondary">Ошибок</Typography>
              </Card>
            </Box>

            {importResult.errors.length > 0 && (
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Ошибки:
                </Typography>
                {importResult.errors.map((err, i) => (
                  <Alert key={i} severity="error" sx={{ mb: 0.5, py: 0, borderRadius: 1 }}>
                    <Typography variant="caption">{err}</Typography>
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {step === 'upload' && (
          <Button onClick={handleClose}>Отмена</Button>
        )}

        {step === 'mapping' && (
          <>
            <Button onClick={() => setStep('upload')}>Назад</Button>
            <Button onClick={handleClose}>Отмена</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={getMappedColumnsCount() === 0}
              startIcon={<ImportIcon />}
              sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #e68900 0%, #e6ac00 100%)',
                },
              }}
            >
              Импортировать ({csvRows.length} строк)
            </Button>
          </>
        )}

        {step === 'importing' && (
          <Typography variant="caption" color="text.secondary">
            Не закрывайте окно во время импорта...
          </Typography>
        )}

        {step === 'done' && (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              background: importResult && importResult.success > 0
                ? 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)'
                : undefined,
            }}
          >
            {importResult && importResult.success > 0 ? 'Готово' : 'Закрыть'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}